import { Clock3, MessageCircleMore, ShieldCheck } from "lucide-react";

import type { MarketplaceRecoveryWindow } from "../../lib/marketplace-recovery-api";

interface Props {
  window: MarketplaceRecoveryWindow;
  incumbentName?: string;
  busy?: boolean;
  onStay: () => void;
  onContinue?: () => void;
  continueAvailable?: boolean;
}

const DEADLINE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function deadline(value: string) {
  return DEADLINE_FORMAT.format(new Date(value));
}

export function RecoveryWindowCard({
  window,
  incumbentName = "your agent",
  busy,
  onStay,
  onContinue,
  continueAvailable = false,
}: Props) {
  const held = window.status === "open" && window.proceed_state === "started";
  const counter =
    window.status === "open" && Boolean(window.counter_offer) && !held;
  const expired = window.status === "expired";
  const released = window.status === "released";

  const title = held
    ? "Your choice is finalizing"
    : counter
      ? `${incumbentName} responded`
      : expired
        ? "Still want to continue?"
        : released
          ? `${incumbentName} wished you well`
          : `Give ${incumbentName} time to respond`;
  const copy = held
    ? `You can still stay with ${incumbentName} while your choice is being finalized.`
    : counter
      ? "They sent a new offer for you to compare. Nothing changes until you choose."
      : expired
        ? "The response window ended. Nothing changed automatically."
        : released
          ? "They released the response window, so you can choose what feels right for you."
          : "Your current relationship stays in place while they have the first chance to respond.";

  return (
    <section
      className="marketplace-surface overflow-hidden"
      data-testid={`recovery-window-${held ? "held" : counter ? "counter" : window.status}`}
    >
      <div className="flex gap-3 p-4 sm:p-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {counter ? (
            <MessageCircleMore className="size-5" />
          ) : released ? (
            <ShieldCheck className="size-5" />
          ) : (
            <Clock3 className="size-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Response window
              </p>
              <h2 className="mt-1 text-base font-semibold text-foreground">
                {title}
              </h2>
            </div>
            {!released && !expired && (
              <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium tabular-nums text-muted-foreground">
                Until {deadline(window.expires_at)}
              </span>
            )}
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
            {copy}
          </p>
          {counter && window.counter_offer?.note && (
            <blockquote className="mt-3 rounded-xl bg-muted/50 px-3 py-2 text-sm text-foreground">
              “{window.counter_offer.note}”
            </blockquote>
          )}
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row">
            {!released && (
              <button
                type="button"
                disabled={busy}
                onClick={onStay}
                className="min-h-11 rounded-xl border border-border bg-popover px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
              >
                Stay with {incumbentName}
              </button>
            )}
            {!held && (
              <button
                type="button"
                disabled={busy || !continueAvailable}
                onClick={onContinue}
                title={
                  !continueAvailable
                    ? "Choice review is coming next"
                    : undefined
                }
                className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {continueAvailable
                  ? "Continue with my choice"
                  : "Review choice to continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
